/**
 * Door check-in link contracts (plan 2026-07-29-003).
 *
 * Two audiences, two shapes:
 *
 * - **Admin** (`CheckInLink`): a school admin reads or rotates the link from the
 *   event management surface. Authenticated.
 * - **Door** (`PublicCheckInDoor`): whoever holds the link opens it at the
 *   event with no login, sees the attendee list, and marks people present.
 *
 * The door shapes are the enforcement point for the plan's no-PII requirement
 * (R8/KTD5). They deliberately carry NO field an email address, phone number, or
 * custom-question answer could occupy — the reduction happens server-side and
 * this contract is what makes a regression a type error rather than a leak. Do
 * not add a contact field here; if the door needs to identify someone more
 * precisely, extend the reference-code path instead.
 *
 * `PublicCheckInAttendeeSchema` is `.strict()` on purpose. Every other schema in
 * this package strips unknown keys, which would silently accept — and discard —
 * an email the server should never have sent. Strict makes that a loud parse
 * failure at the boundary instead.
 */

import { z } from 'zod'
import {
  TicketedEventAdmissionOriginSchema,
  TicketedEventAdmissionStatusSchema
} from '../../schemas/entities/ticketed-event-admission'
import { ADMIN_MINT_IDEMPOTENCY_KEY_MAX_LENGTH } from './admissions'

/**
 * One attendee row on the door surface. Keyed by order publicId because
 * check-in state lives per order, not per person.
 *
 * `checkedIn` is all-or-nothing (KTD11): true only when every LIVE SEAT on the
 * order is checked in. An order can be half-checked-in in the database, but the
 * door does not surface partial state — "half arrived" would confuse door staff
 * more than it informs them. `checkedInAt` is the LATEST of those seats' stamps
 * when `checkedIn` is true, else null. An order holding no live seats — every
 * one cancelled or voided — is not checked in; nobody is left to admit.
 *
 * Both fields were derived from the order ITEMS' stamps until the P3 read flip
 * (SCH-1622), and `checkedInAt` was then the earliest rather than the latest.
 * KTD6 names this field outright, and R46 does not hold the change back: R46
 * covers what a shipped mobile build reads off the ORDER payload, while this row
 * is consumed by a web surface that redeploys on every merge. The single
 * derivation both rollups share is
 * `apps/backend/src/services/ticketed-events/shared/admission-derivation.ts`.
 *
 * `totalQuantity` is the order's LIVE seat count, not what was bought
 * (SCH-2507): a seat that was cancelled or voided no longer counts at the door.
 * An order holding no live seats is not listed at all, and marking it is
 * refused with a 409. Like `checkedInAt`, this field is read only by the web
 * door, so the change is not held back by R46.
 *
 * This row carries NO per-seat entries and the object is `.strict()`, so it
 * cannot grow one by accident. Since U8 the door renders `groups` instead, and
 * this list is kept, unchanged in meaning, for one release so a web build from
 * before U8 still works against the new backend (the two deploy separately).
 */
export const PublicCheckInAttendeeSchema = z
  .object({
    orderPublicId: z.string(),
    firstName: z.string(),
    // Single character, server-reduced from the buyer's last name. Never the
    // full surname — that is the point of the reduction.
    lastInitial: z.string().max(1),
    referenceCode: z.string(),
    ticketTypeNames: z.array(z.string()),
    totalQuantity: z.number().int(),
    checkedIn: z.boolean(),
    checkedInAt: z.string().nullable()
  })
  .strict()
export type PublicCheckInAttendee = z.infer<typeof PublicCheckInAttendeeSchema>

/**
 * Just enough event context for the holder to confirm they opened the right
 * door. Not the public event DTO — no description, cover image, ticket types,
 * capacity, or organizer.
 */
export const PublicCheckInEventSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    startsAt: z.string(),
    locationText: z.string().nullable()
  })
  .strict()
export type PublicCheckInEvent = z.infer<typeof PublicCheckInEventSchema>

/**
 * The statuses a door row can show. `voided` is never listed: a voided ticket
 * never legitimately existed. `cancelled` and `deactivated` are listed so the
 * volunteer can say why someone cannot come in (R23).
 */
export const PublicCheckInRegistrationStatusSchema = TicketedEventAdmissionStatusSchema.exclude(['voided'])
export type PublicCheckInRegistrationStatus = z.infer<typeof PublicCheckInRegistrationStatusSchema>

/**
 * One ticket at the door (U8 — R21). The same no-PII rule as the order row:
 * the holder's first name and one initial, the ticket type, how the ticket came
 * to exist, its status and its stamp — no email, no answers. A ticket with no
 * holder named yet has a null first name and an empty initial.
 *
 * Check-in acts on this row, so `checkedInAt` is the ticket's own stamp, not a
 * rollup.
 */
export const PublicCheckInRegistrationSchema = z
  .object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastInitial: z.string().max(1),
    ticketTypeName: z.string(),
    origin: TicketedEventAdmissionOriginSchema,
    status: PublicCheckInRegistrationStatusSchema,
    checkedInAt: z.string().nullable()
  })
  .strict()
export type PublicCheckInRegistration = z.infer<typeof PublicCheckInRegistrationSchema>

/**
 * The door groups tickets the way people arrive: one group per order, keeping
 * the buyer display fields the order row has, and one group per ticket staff
 * added with no order behind it (a comp, a box-office sale, a walk-up),
 * labelled by its `origin`.
 */
export const PublicCheckInGroupSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('order'),
      orderPublicId: z.string(),
      firstName: z.string(),
      lastInitial: z.string().max(1),
      referenceCode: z.string(),
      registrations: z.array(PublicCheckInRegistrationSchema)
    })
    .strict(),
  z
    .object({
      kind: z.literal('staff_added'),
      origin: TicketedEventAdmissionOriginSchema,
      registrations: z.array(PublicCheckInRegistrationSchema)
    })
    .strict()
])
export type PublicCheckInGroup = z.infer<typeof PublicCheckInGroupSchema>

/**
 * A ticket type the door's walk-up form may offer: visible, not cancelled, and
 * free (R45). `remaining` is what is left of it, capped by the event's own
 * remaining capacity; a type at zero is listed so the form can say it is full.
 */
export const PublicCheckInWalkUpTicketTypeSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    remaining: z.number().int()
  })
  .strict()
export type PublicCheckInWalkUpTicketType = z.infer<typeof PublicCheckInWalkUpTicketTypeSchema>

/** The whole door payload: which event, who is expected, how many are in. */
export const PublicCheckInDoorSchema = z
  .object({
    event: PublicCheckInEventSchema,
    /** The pre-U8 order rows. Kept for one release; see `PublicCheckInAttendeeSchema`. */
    attendees: z.array(PublicCheckInAttendeeSchema),
    /** Every ticket at the door, grouped by order or listed alone (U8). */
    groups: z.array(PublicCheckInGroupSchema),
    /** Empty when the event is cancelled or archived, or has no free visible type. */
    walkUpTicketTypes: z.array(PublicCheckInWalkUpTicketTypeSchema),
    // LIVE ticket counts across every group, orders and staff-added alike: a
    // cancelled ticket stops counting. `checkedInSeats` counts each live ticket
    // that is in, so a half-arrived order counts the half that arrived.
    totalSeats: z.number().int(),
    checkedInSeats: z.number().int()
  })
  .strict()
export type PublicCheckInDoor = z.infer<typeof PublicCheckInDoorSchema>

/**
 * Request body for the pre-U8 order route, which takes the order in its path.
 * Explicit intent, never a toggle (KTD9): `true` is idempotent on an order
 * already checked in; `false` is the correction path for a mistaken check-in.
 */
export const MarkCheckInRequestSchema = z.object({
  present: z.boolean()
})
export type MarkCheckInRequest = z.infer<typeof MarkCheckInRequestSchema>

/**
 * The door's mark body (U8): ONE ticket, or every active ticket on one order
 * ("check in all"). Exactly one of the two ids; a cancelled, voided or
 * deactivated ticket on the order is skipped, never stamped.
 */
export const MarkDoorCheckInRequestSchema = z.union([
  z.object({ admissionPublicId: z.string().min(1), present: z.boolean() }).strict(),
  z.object({ orderPublicId: z.string().min(1), present: z.boolean() }).strict()
])
export type MarkDoorCheckInRequest = z.infer<typeof MarkDoorCheckInRequestSchema>

/** Result of marking an order, so the door can distinguish "did it" from "already was". */
export const MarkCheckInResultSchema = z
  .object({
    attendee: PublicCheckInAttendeeSchema,
    // True when the order was already in the requested state and nothing
    // changed. The door renders "already checked in" from this rather than
    // inferring it client-side.
    alreadyInRequestedState: z.boolean(),
    /** The order's door rows after the mark (U8). */
    registrations: z.array(PublicCheckInRegistrationSchema)
  })
  .strict()
export type MarkCheckInResult = z.infer<typeof MarkCheckInResultSchema>

/** Result of marking one ticket (U8). */
export const MarkRegistrationCheckInResultSchema = z
  .object({
    registration: PublicCheckInRegistrationSchema,
    /** True when the ticket was already in the requested state; its original stamp is kept. */
    alreadyInRequestedState: z.boolean()
  })
  .strict()
export type MarkRegistrationCheckInResult = z.infer<typeof MarkRegistrationCheckInResultSchema>

/**
 * Why the door refused a ticket (R23), in the 409 `REGISTRATION_NOT_ACTIVE`
 * error's `details`. A deactivated ticket is refused with its own reason, which
 * implies no refund (AE3).
 */
export const RegistrationNotActiveReasonSchema = z.enum(['cancelled', 'voided', 'deactivated'])
export type RegistrationNotActiveReason = z.infer<typeof RegistrationNotActiveReasonSchema>

export const RegistrationNotActiveDetailsSchema = z.object({ reason: RegistrationNotActiveReasonSchema })
export type RegistrationNotActiveDetails = z.infer<typeof RegistrationNotActiveDetailsSchema>

/**
 * The door's walk-up (U8 — R22, R45): one free ticket for someone standing at
 * the door, added and checked in at once. The key is generated once per form
 * open, so a double submit adds one ticket.
 */
export const DoorWalkUpRequestSchema = z
  .object({
    ticketTypeId: z.string().min(1),
    firstName: z.string().trim().min(1).max(255),
    lastName: z.string().trim().min(1).max(255),
    email: z.string().trim().toLowerCase().email().max(320).optional(),
    idempotencyKey: z.string().min(1).max(ADMIN_MINT_IDEMPOTENCY_KEY_MAX_LENGTH)
  })
  .strict()
export type DoorWalkUpRequest = z.infer<typeof DoorWalkUpRequestSchema>

/** The ticket the walk-up added, already checked in. */
export const DoorWalkUpResultSchema = z
  .object({
    registration: PublicCheckInRegistrationSchema
  })
  .strict()
export type DoorWalkUpResult = z.infer<typeof DoorWalkUpResultSchema>

/**
 * Admin view of an event's door link. `url` is the absolute link an admin
 * copies and hands to an event manager out of band.
 */
export const CheckInLinkSchema = z
  .object({
    url: z.string(),
    token: z.string(),
    mintedAt: z.string(),
    // When the link stops resolving — `startsAt` + the door window. Rendered on
    // the admin card because the holder of an expired link cannot be told why
    // it stopped working (R13 keeps every failure indistinguishable).
    expiresAt: z.string()
  })
  .strict()
export type CheckInLink = z.infer<typeof CheckInLinkSchema>
